/**
 * Status / treatment pills for answer tables.
 *
 * Monitoring tables carry a scannable column — a covenant status, or an
 * in/out treatment — whose cell leads with one token from a closed set and
 * then, usually, explains itself: `Breach — FCCR by 0.29x`, `**in** —
 * uncapped`, `Memo: not a covenant figure`. The pill is that leading token;
 * the remainder stays plain text beside it.
 *
 * Matching is exact on the leading token, never fuzzy: `Watchlist`,
 * `Inbound` and `Compliance` are ordinary words that happen to start with a
 * vocabulary term, and a cell that is only a reason is not a status.
 */

/** Colour families the pill renders in. */
export type StatusTone = "red" | "amber" | "green" | "teal" | "grey";

export interface StatusPillMatch {
	tone: StatusTone;
	/** The vocabulary term in its canonical casing. */
	label: string;
	/** Everything after the separator, trimmed. Empty when the cell is just the token. */
	rest: string;
}

/**
 * The closed vocabulary, keyed by the lowercased token. Two families share
 * the table: covenant status (`Breach`/`Watch`/`Compliant` and the synonyms
 * answers reach for) and EBITDA-walk treatment (`In`/`Out`/`Permitted`/…).
 *
 * The harness asks for `Breach`/`Watch`/`Compliant`; the synonyms beside them
 * — `Offside`, `Tight`, `Monitor`, `Pass` — are what generations actually
 * write. A near-synonym left out of this list renders as plain text in a
 * column of pills, which looks broken, so the renderer tolerates them rather
 * than the column being half-flagged.
 */
const VOCABULARY: ReadonlyArray<{ label: string; tone: StatusTone }> = [
	{ label: "Breach", tone: "red" },
	{ label: "Offside", tone: "red" },
	{ label: "Watch", tone: "amber" },
	{ label: "Tight", tone: "amber" },
	{ label: "Monitor", tone: "amber" },
	{ label: "Compliant", tone: "green" },
	{ label: "Pass", tone: "green" },
	{ label: "Permitted", tone: "green" },
	{ label: "In", tone: "green" },
	{ label: "Covenant basis", tone: "teal" },
	{ label: "Excluded", tone: "grey" },
	{ label: "Out", tone: "grey" },
	{ label: "Memo", tone: "grey" },
	{ label: "Reported", tone: "grey" },
];

const BY_LOWER: ReadonlyMap<string, { label: string; tone: StatusTone }> =
	new Map(VOCABULARY.map((entry) => [entry.label.toLowerCase(), entry]));

/**
 * Separators an answer puts between the token and its reason. Spaced dashes
 * only — an unspaced hyphen belongs to compounds like `non-compliant`.
 */
const SEPARATORS = [" — ", " – ", ":", ","];

/** `**In**`, `*In*`, `__In__`, `_In_` → `In`. Emphasis is decoration here. */
function stripEmphasis(text: string): string {
	let out = text.trim();
	for (const marker of ["**", "__", "*", "_"]) {
		while (
			out.length > 2 * marker.length &&
			out.startsWith(marker) &&
			out.endsWith(marker)
		) {
			out = out.slice(marker.length, -marker.length).trim();
		}
	}
	return out;
}

/**
 * Split a cell into its leading token and the remainder, at whichever
 * separator comes first — `TIGHT — leverage, ICR` splits on the dash, not on
 * the comma inside the reason.
 */
function splitLeading(text: string): { head: string; rest: string } {
	let cut = -1;
	let width = 0;
	for (const separator of SEPARATORS) {
		const at = text.indexOf(separator);
		if (at !== -1 && (cut === -1 || at < cut)) {
			cut = at;
			width = separator.length;
		}
	}
	if (cut === -1) return { head: text, rest: "" };
	return { head: text.slice(0, cut), rest: text.slice(cut + width).trim() };
}

/**
 * Classify a table cell. Returns the pill's tone, its canonical label and the
 * text that follows it, or `null` when the leading token is not vocabulary —
 * in which case the caller renders the cell unchanged.
 */
export function classifyStatusCell(
	text: string | null | undefined,
): StatusPillMatch | null {
	if (!text) return null;
	const { head, rest } = splitLeading(text.trim());
	const entry = BY_LOWER.get(stripEmphasis(head).toLowerCase());
	if (!entry) return null;
	return { tone: entry.tone, label: entry.label, rest };
}
